import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Award,
  CheckCircle2,
  Clock,
  FileCheck2,
  Globe2,
  GraduationCap,
  Quote,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Users2,
} from "lucide-react";

import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";

// Premium Unsplash assets
const HERO_BG =
  "https://images.unsplash.com/photo-1742093651495-9dd169b67980?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBicmlnaHQlMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwZGF5dGltZXxlbnwwfHx8fDE3NzgxMDMzOTJ8MA&ixlib=rb-4.1.0&q=85";
// Young confident Indian medical doctor with stethoscope
const STUDENT_IMG =
  "https://plus.unsplash.com/premium_photo-1661745702156-cd4b0382455f?fm=jpg&q=85&w=1400&auto=format&fit=crop&ixlib=rb-4.1.0";
// Senior doctor portrait
const DOC_IMG =
  "https://plus.unsplash.com/premium_photo-1681996359725-06262b082c27?fm=jpg&q=85&w=1400&auto=format&fit=crop&ixlib=rb-4.1.0";

const MARQUEE = [
  "Crimea Federal University",
  "Tbilisi State Medical Univ.",
  "Samarkand State Medical",
  "Kazakh National Medical",
  "Osh State University",
  "Davao Medical School",
  "Bashkir State Medical",
  "Asian Medical Institute",
];

const TESTIMONIALS = [
  {
    name: "Aanya Sharma",
    score: "612 NEET",
    quote:
      "MedAdmits got me 70% off in tuition. My counsellor literally walked me through visa, hostel, even SIM cards.",
    img: "https://customer-assets.emergentagent.com/job_mbbs-lead-hub/artifacts/wemk1yok_Aanya.jpg",
  },
  {
    name: "Rohit",
    score: "548 NEET",
    quote:
      "I was anxious about going abroad. The 10-min test made it real and the team called within 24 hours.",
    img: "https://customer-assets.emergentagent.com/job_mbbs-lead-hub/artifacts/84lva538_Rohit.jpg",
  },
  {
    name: "Prabhjot Kaur",
    score: "489 NEET",
    quote:
      "Zero hidden fees. They mailed the exact total cost in writing. Parents were finally convinced.",
    img: "https://customer-assets.emergentagent.com/job_mbbs-lead-hub/artifacts/gh2g72l1_Prabhjot.jpg",
  },
];

function CountUp({ to, suffix = "", duration = 1400 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return (
    <span>
      {n.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function LandingPage() {
  const [slabs, setSlabs] = useState([]);
  useEffect(() => {
    api
      .get("/scholarship-slabs")
      .then((r) => setSlabs(r.data.slabs))
      .catch(() => {});
  }, []);

  // Capture UTM into localStorage on first landing visit
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      referrer: document.referrer || "",
    };
    if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
      localStorage.setItem("medix_utm", JSON.stringify(utm));
    } else if (!localStorage.getItem("medix_utm") && utm.referrer) {
      localStorage.setItem("medix_utm", JSON.stringify(utm));
    }
  }, []);

  // A/B test: hero CTA copy
  const [variant, setVariant] = useState("A");
  useEffect(() => {
    let v = localStorage.getItem("medix_cta_variant");
    if (v !== "A" && v !== "B") {
      v = Math.random() < 0.5 ? "A" : "B";
      localStorage.setItem("medix_cta_variant", v);
    }
    setVariant(v);
    // Send impression once per session
    if (!sessionStorage.getItem("medix_cta_imp")) {
      sessionStorage.setItem("medix_cta_imp", "1");
      api.post("/experiments/impression", { variant: v }).catch(() => {});
    }
  }, []);
  const heroCtaLabel =
    variant === "B" ? "Start Scholarship Test" : "Register for Scholarship";

  return (
    <div className="min-h-screen bg-white">
      {/* Premium top header */}
      <header className="sticky top-0 z-40 w-full border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
          <Link to="/scholars" className="flex items-center gap-2.5" data-testid="logo-link">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#0A2540]">
              <Stethoscope className="h-5 w-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-base font-bold tracking-tight text-[#0A2540]">
                MedAdmits
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">
                Future Doctors Start Here
              </div>
            </div>
          </Link>
          <nav className="hidden items-center gap-7 text-sm text-slate-600 md:flex">
            <a href="#scholarships" className="hover:text-[#0A2540]">Scholarships</a>
            <a href="#how" className="hover:text-[#0A2540]">How it works</a>
            <a href="#voices" className="hover:text-[#0A2540]">Stories</a>
            <Link to="/admin/login" className="text-slate-500 hover:text-[#0A2540]" data-testid="admin-login-nav">
              Consultancy Login
            </Link>
          </nav>
          <Link
            to="/register"
            className="hidden items-center gap-1.5 rounded-md bg-[#0A2540] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1E3A5F] md:inline-flex"
            data-testid="header-register-cta"
          >
            Take the test
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* HERO — asymmetric luxury */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/85 to-white" />
          <div className="absolute inset-0 bg-[radial-gradient(rgba(10,37,64,0.05)_1px,transparent_1px)] [background-size:3px_3px]" />
        </div>

        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-12 sm:px-6 sm:pt-16 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-32 lg:pt-24">
          <div className="fade-up lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#C9A961]/40 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0A2540]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#C9A961]" />
              {variant === "B"
                ? "Limited seats · 2026 intake"
                : "Batch 2026 · Scholarships up to ₹4,00,000"}
            </div>

            {variant === "B" ? (
              <>
                <h1
                  className="font-display mt-6 text-[40px] font-bold leading-[1.05] tracking-[-0.03em] text-[#0A2540] sm:text-[56px] lg:text-[64px]"
                  data-testid="hero-title-b"
                >
                  Get a Scholarship for{" "}
                  <span className="relative inline-block">
                    <span className="text-[#0A2540]">MBBS Abroad</span>
                    <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[#C9A961]" />
                  </span>
                  <br />
                  up to <span className="text-[#C9A961]">₹5 Lakhs</span>
                  <span className="text-slate-400">.</span>
                </h1>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-700 sm:text-lg">
                  Take India&rsquo;s MBBS Abroad{" "}
                  <strong className="text-[#0A2540]">
                    Scholarship Eligibility Test
                  </strong>{" "}
                  &mdash; designed for NEET aspirants targeting global medical careers.
                </p>
                <ul
                  className="mt-7 grid max-w-2xl gap-2.5 text-[15px] text-slate-700 sm:grid-cols-2"
                  data-testid="hero-bullets-b"
                >
                  {[
                    "NEET Qualified & NEET Appearing students eligible",
                    "Russia, Europe, Kazakhstan, Kyrgyzstan, Georgia & more",
                    "Scholarship based on time taken & accuracy",
                    "Limited scholarship seats for 2026 intake",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-[5px] flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700">
                        <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 fill-current">
                          <path d="M4.6 8.7L2 6.1l1.1-1.1 1.5 1.5L8.9 2l1.1 1.1z" />
                        </svg>
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <h1 className="font-display mt-6 text-[44px] font-bold leading-[1.02] tracking-[-0.04em] text-[#0A2540] sm:text-6xl lg:text-7xl">
                  The white coat
                  <br />
                  <span className="relative inline-block">
                    <span className="text-[#0A2540]">starts here</span>
                    <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[#C9A961]" />
                  </span>
                  <span className="text-slate-400">.</span>
                </h1>
                <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                  India&rsquo;s most rigorous Scholarship Test for MBBS abroad.
                  Take a 10-minute aptitude assessment, unlock guaranteed merit
                  tuition cuts at <strong className="text-[#0A2540]">47 vetted
                  universities</strong>, and meet a senior counsellor &mdash; all
                  before your friends finish reading their NEET cutoff PDF.
                </p>
              </>
            )}

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/register"
                className="group inline-flex h-14 items-center justify-center gap-2 rounded-md bg-[#0A2540] px-7 text-sm font-semibold tracking-wide text-white transition-all hover:-translate-y-0.5 hover:bg-[#1E3A5F] hover:shadow-[0_18px_40px_-15px_rgba(10,37,64,0.6)]"
                data-testid="hero-register-cta"
                data-variant={variant}
              >
                {heroCtaLabel}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="#how"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-7 text-sm font-medium text-[#0A2540] transition-colors hover:border-[#0A2540]"
                data-testid="hero-learn-more"
              >
                See how it works
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>

            <div className="mt-12 grid max-w-2xl grid-cols-3 gap-x-6 gap-y-2 border-t border-slate-200 pt-7">
              {[
                { v: 12400, suffix: "+", l: "Students placed" },
                { v: 47, suffix: "", l: "Vetted universities" },
                { v: 98, suffix: "%", l: "Visa success" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display text-3xl font-bold tracking-tight text-[#0A2540] sm:text-4xl">
                    <CountUp to={s.v} suffix={s.suffix} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — luxury card stack */}
          <div className="fade-up relative lg:col-span-5">
            <div className="absolute -left-6 -top-6 hidden h-36 w-36 rounded-full bg-[#C9A961]/15 blur-3xl lg:block" />
            <div className="absolute -bottom-12 -right-6 hidden h-44 w-44 rounded-full bg-[#0A2540]/10 blur-3xl lg:block" />

            <div className="relative">
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(10,37,64,0.45)]">
                <img
                  src={STUDENT_IMG}
                  alt="Confident young Indian medical doctor in white coat with stethoscope"
                  className="h-[420px] w-full object-cover sm:h-[520px]"
                  loading="eager"
                />
              </div>

              {/* Floating eligibility card */}
              <div className="absolute -bottom-8 left-4 right-4 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur-md sm:-left-6 sm:right-auto sm:w-72 sm:p-5">
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#166534]">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Free eligibility check
                </div>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-700">
                  10-minute aptitude test. Results &amp; scholarship slab
                  emailed within 48 hours.
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="h-3.5 w-3.5" /> No retakes · Refresh-safe
                </div>
              </div>

              {/* Floating award chip */}
              <div className="absolute -top-4 -right-2 hidden rotate-3 items-center gap-2 rounded-full border border-[#C9A961]/40 bg-white px-4 py-2 text-xs font-semibold text-[#0A2540] shadow-md sm:inline-flex">
                <Award className="h-4 w-4 text-[#C9A961]" />
                NMC · WHO · WFME
              </div>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="border-y border-slate-200 bg-[#0A2540]">
          <div className="overflow-hidden py-3">
            <div className="flex gap-12 whitespace-nowrap [animation:marquee_36s_linear_infinite]">
              {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((u, i) => (
                <span
                  key={i}
                  className="text-xs uppercase tracking-[0.32em] text-white/60"
                >
                  ◆ {u}
                </span>
              ))}
            </div>
          </div>
          <style>{`@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
        </div>
      </section>

      {/* COUNTRIES — destination cards */}
      <section
        id="countries"
        className="border-b border-slate-200 bg-white py-20 sm:py-24"
        data-testid="countries-section"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
                Where you can study
              </div>
              <h2 className="font-display mt-3 text-3xl font-semibold leading-[1.05] tracking-[-0.025em] text-[#0A2540] sm:text-5xl">
                15+ Countries.
                <br />
                One scholarship test.
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-slate-600">
              Every country below has NMC-recognised universities in our
              partner network. Your scholarship slab applies across all
              destinations &mdash; pick the one that fits your goals.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                flag: "🇷🇺",
                name: "Russia",
                desc: "Affordable universities with global recognition.",
                accent: "from-rose-100 via-white to-white",
              },
              {
                flag: "🇰🇿",
                name: "Kazakhstan",
                desc: "Popular among Indian NEET students with budget-friendly fees.",
                accent: "from-sky-100 via-white to-white",
              },
              {
                flag: "🇰🇬",
                name: "Kyrgyzstan",
                desc: "Low-cost MBBS with Indian food & hostel facilities.",
                accent: "from-amber-100 via-white to-white",
              },
              {
                flag: "🇬🇪",
                name: "Georgia",
                desc: "European-standard education with modern infrastructure.",
                accent: "from-violet-100 via-white to-white",
              },
              {
                flag: "🇺🇿",
                name: "Uzbekistan",
                desc: "Fast-growing destination for MBBS abroad.",
                accent: "from-emerald-100 via-white to-white",
              },
              {
                flag: "🇪🇺",
                name: "Europe",
                desc: "European-standard MBBS with global recognition and quality clinical exposure.",
                accent: "from-orange-100 via-white to-white",
              },
              {
                flag: "🇵🇭",
                name: "Philippines",
                desc: "English-medium MBBS pathway with US-based curriculum.",
                accent: "from-blue-100 via-white to-white",
              },
              {
                flag: "✦",
                name: "And growing",
                desc: "We add new vetted universities every quarter. Talk to a counsellor for the latest list.",
                accent: "from-slate-100 via-white to-white",
                muted: true,
              },
            ].map((c) => (
              <div
                key={c.name}
                className={`group relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br ${c.accent} p-5 transition-all hover:-translate-y-1 hover:border-[#C9A961]/50 hover:shadow-[0_18px_40px_-20px_rgba(10,37,64,0.35)]`}
                data-testid={`country-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="text-3xl leading-none">{c.flag}</div>
                <h3
                  className={`font-display mt-3 text-lg font-semibold tracking-tight ${
                    c.muted ? "text-slate-500" : "text-[#0A2540]"
                  }`}
                >
                  {c.name}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  {c.desc}
                </p>
                {!c.muted && (
                  <div className="mt-4 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C9A961] opacity-0 transition-opacity group-hover:opacity-100">
                    Eligible for scholarship
                    <ArrowRight className="h-3 w-3" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-[#0A2540] px-7 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-[#1E3A5F]"
              data-testid="countries-register-cta"
            >
              Check my eligibility
              <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="text-xs text-slate-500">
              Free · Takes 10 minutes · Results in 48 hours
            </span>
          </div>
        </div>
      </section>

      {/* WHY — bento */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
              The MedAdmits Difference
            </div>
            <h2 className="font-display mt-3 text-3xl font-semibold leading-[1.05] tracking-[-0.025em] text-[#0A2540] sm:text-5xl">
              Dreaming of becoming
              <br />
              a doctor…
              <br />
              <span className="text-[#C9A961]">but worried about MBBS fees?</span>
            </h2>
            <p className="mt-6 max-w-md text-slate-600">
              Take the <strong className="text-[#0A2540]">FREE MBBS Abroad Scholarship &amp; Budget Eligibility Test</strong> and unlock a personalised scholarship tier in under 10 minutes. The scholarship tiers below are computed automatically from your test percentage.
            </p>
          </div>
          <div className="grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 lg:col-span-7 lg:grid-cols-2">
            {[
              { icon: Award, title: "Merit-first scholarships", body: "Score above 40% on our aptitude test to instantly qualify. The higher you score, the bigger your slab." },
              { icon: Globe2, title: "47 vetted universities", body: "Russia · Georgia · Uzbekistan · Kazakhstan · Kyrgyzstan · Philippines — every campus visited in person." },
              { icon: ShieldCheck, title: "Zero-shock pricing", body: "Total 6-year cost shared in writing before you commit. No agent commissions buried in tuition." },
              { icon: GraduationCap, title: "FMGE / NEXT prep", body: "Free India-licensure coaching from year 1 — built into your admission package." },
            ].map((f) => (
              <div key={f.title} className="group bg-white p-7 transition-colors hover:bg-[#F8F9FA] sm:p-9">
                <f.icon className="h-6 w-6 text-[#0A2540]" strokeWidth={1.7} />
                <h3 className="font-display mt-5 text-lg font-semibold text-[#0A2540]">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCHOLARSHIP SLABS */}
      <section id="scholarships" className="bg-[#0A2540] py-24 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid items-end gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
                Auto-computed tiers
              </div>
              <h2 className="font-display mt-3 text-3xl font-semibold leading-[1.05] tracking-[-0.025em] sm:text-5xl">
                Your test score = your scholarship.
              </h2>
            </div>
            <p className="text-sm text-white/70 lg:col-span-5">
              The moment you submit, our engine maps your accuracy to a
              tier and notifies our counsellor team. No human gatekeeping.
            </p>
          </div>

          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {(slabs.length
              ? slabs
              : [
                  { tier: "Platinum", min_pct: 85, amount: 400000 },
                  { tier: "Gold", min_pct: 70, amount: 250000 },
                  { tier: "Silver", min_pct: 55, amount: 125000 },
                  { tier: "Bronze", min_pct: 40, amount: 50000 },
                ]
            ).map((s, i) => (
              <div
                key={s.tier}
                className="group bg-[#0A2540] p-7 transition-colors hover:bg-[#0E2E50]"
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono-clock text-xs text-white/40">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div
                    className={`h-2 w-2 rounded-full ${
                      i === 0
                        ? "bg-[#C9A961]"
                        : i === 1
                          ? "bg-amber-300"
                          : i === 2
                            ? "bg-slate-300"
                            : "bg-orange-300"
                    }`}
                  />
                </div>
                <div className="font-display mt-6 text-2xl font-semibold tracking-tight">
                  {s.tier}
                </div>
                <div className="font-display mt-7 text-3xl font-bold text-white">
                  ₹{(s.amount / 100000).toFixed(2).replace(/\.00$/, "")}L
                </div>
                <div className="mt-1 text-xs text-white/60">tuition cut, 6-yr program</div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Link
              to="/register"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-white px-7 text-sm font-semibold text-[#0A2540] transition-transform hover:-translate-y-0.5"
              data-testid="slabs-register-cta"
            >
              Start Scholarship Test
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-y border-slate-200 bg-[#F8F9FA] py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
              The Process
            </div>
            <h2 className="font-display mt-3 text-3xl font-semibold leading-[1.05] tracking-[-0.025em] text-[#0A2540] sm:text-5xl">
              Four steps. <span className="text-slate-400">Zero guesswork.</span>
            </h2>
          </div>

          <ol className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "01", icon: Users2, t: "Register", d: "Share your NEET score & preferred country in 60 seconds." },
              { n: "02", icon: Clock, t: "10-min Test", d: "Take the timed MCQ aptitude test. Mobile-first, refresh-safe." },
              { n: "03", icon: FileCheck2, t: "Unlock Slab", d: "Upload your NEET scorecard to verify and unlock a higher scholarship slab." },
              { n: "04", icon: Stethoscope, t: "Get Matched", d: "Counsellors call within 48 hrs with your scholarship slab & shortlist." },
            ].map((s) => (
              <li
                key={s.n}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="font-display absolute -right-2 -top-2 select-none text-7xl font-bold text-slate-100">
                  {s.n}
                </div>
                <s.icon className="relative h-6 w-6 text-[#0A2540]" strokeWidth={1.7} />
                <h3 className="font-display relative mt-5 text-lg font-semibold text-[#0A2540]">
                  {s.t}
                </h3>
                <p className="relative mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
              </li>
            ))}
          </ol>

          <div className="mt-12">
            <Link
              to="/register"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-[#0A2540] px-7 text-sm font-semibold text-white transition-colors hover:bg-[#1E3A5F]"
              data-testid="how-register-cta"
            >
              Start free test
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="voices" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-10">
        <div className="grid items-end gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
              From the batch of 2025
            </div>
            <h2 className="font-display mt-3 text-3xl font-semibold leading-[1.05] tracking-[-0.025em] text-[#0A2540] sm:text-5xl">
              They took the test.<br />
              <span className="text-slate-400">Now they wear the white coat.</span>
            </h2>
          </div>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <figure
              key={t.name}
              className="rounded-2xl border border-slate-200 bg-white p-7 transition-shadow hover:shadow-md"
            >
              <Quote className="h-5 w-5 text-[#C9A961]" strokeWidth={2} />
              <blockquote className="mt-4 text-sm leading-relaxed text-slate-700">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-4">
                <img
                  src={t.img}
                  alt={t.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div>
                  <div className="text-sm font-semibold text-[#0A2540]">{t.name}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    {t.score}
                  </div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* MENTOR */}
      <section className="bg-[#F8F9FA] py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border border-[#C9A961]/30" />
                <img
                  src={DOC_IMG}
                  alt="MedAdmits Head of Counselling"
                  className="relative h-[480px] w-full rounded-2xl object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
                A note from our Head of Counselling
              </div>
              <blockquote className="font-display mt-5 text-2xl font-medium leading-tight tracking-[-0.02em] text-[#0A2540] sm:text-4xl">
                &ldquo;The wrong university choice can quietly cost a student
                six years of life. We built MedAdmits so no parent in India
                has to gamble with their child&rsquo;s career again.&rdquo;
              </blockquote>
              <div className="mt-8 flex items-center gap-4 border-t border-slate-200 pt-5">
                <div>
                  <div className="text-sm text-slate-500">
                    Find Affordable Paths to Your Doctor Dream with MedAdmits Scholarships
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-[#0A2540] p-10 sm:p-16">
            <div className="absolute -bottom-24 -right-12 h-72 w-72 rounded-full bg-[#C9A961]/15 blur-3xl" />
            <div className="absolute -top-32 -left-12 h-72 w-72 rounded-full bg-white/5 blur-3xl" />
            <div className="relative grid gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                  <Sparkles className="h-3.5 w-3.5 text-[#C9A961]" />
                  Scholarship Test closing in 14 Days
                </div>
                <h2 className="font-display mt-5 text-3xl font-bold leading-[1.05] tracking-[-0.025em] text-white sm:text-5xl">
                  Your seat is one short test away.
                </h2>
                <p className="mt-3 max-w-xl text-white/70">
                  Free registration · 10-min test · Scholarship slab + shortlist
                  in your inbox in 48 hours.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to="/register"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-md bg-white px-7 text-sm font-semibold text-[#0A2540] transition-transform hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-15px_rgba(255,255,255,0.4)]"
                  data-testid="cta-register-final"
                >
                  Register for Scholarship
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
